import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Checkbox,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  SxProps,
} from '@mui/material';

import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';

import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';
import {
  getChatState,
  startConversation,
  toggleNewChat,
} from '../../../../store/V2/ChatSlice';
import { IDENTIFIED_USER_ROLES, IUser } from '../../../../store/UserSlice';
import { getCoursesState } from '../../../../store/V2/CoursesSlice';
import { getCourseStudentsState } from '../../../../store/V2/CourseStudentsSlice';

interface NewConversationProps {
  searchText: string;
}

export const avatarStyles: SxProps = {
  width: '35px',
  height: '35px',
};

const NewConversation: React.FC<NewConversationProps> = ({ searchText }) => {
  const dispatch = useDispatch();
  const urlParams = useParams();

  // Store
  const { userCourse } = useAppSelector(getCoursesState);
  const { courseStudents } = useAppSelector(getCourseStudentsState);
  const { isConversationCreating } = useAppSelector(getChatState);

  // State
  const [users, setUsers] = useState<IUser[]>([]);
  const [userOptions, setUserOptions] = useState<IUser[]>([]);
  const [isLoading, setLoading] = useState(false);

  const courseId = urlParams.id;

  const filteredUserOptions = userOptions.filter((user) =>
    `${user.firstname} ${user.lastname}`
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

  useEffect(() => {
    if (userCourse) {
      const { identifiedCourseUserRole, tutors } = userCourse;
      let newUserOptions: IUser[] = [];

      if (
        identifiedCourseUserRole === IDENTIFIED_USER_ROLES.TUTOR &&
        courseStudents.length
      ) {
        newUserOptions = courseStudents.map(({ student }) => student);
      } else if (
        identifiedCourseUserRole === IDENTIFIED_USER_ROLES.STUDENT &&
        tutors?.length
      ) {
        newUserOptions = tutors.map(({ tutor }) => tutor);
      }
      setUserOptions(newUserOptions);
    }
  }, [courseStudents]);

  useEffect(() => {
    if (isLoading && !isConversationCreating) {
      dispatch(toggleNewChat());
      setLoading(false);
    }
  }, [isConversationCreating]);

  const handleUserCheck = (userItem: IUser) => {
    setUsers((prevUsers) => {
      const isSelected = prevUsers.some(({ _id }) => _id === userItem._id);

      return isSelected
        ? prevUsers.filter(({ _id }) => _id !== userItem._id)
        : [...prevUsers, userItem];
    });
  };

  const handleStartConversation = async () => {
    if (!courseId || users.length === 0) return;

    setLoading(true);

    const usersIds = users.map(({ _id }) => _id);

    await dispatch(startConversation({ course: courseId, users: usersIds }));
  };

  return (
    <Stack height="100%" justifyContent="space-between">
      <List sx={{ overflowY: 'auto' }}>
        {filteredUserOptions.map((userItem) => {
          const fullName = `${userItem.firstname} ${userItem.lastname || ''}`;
          return (
            <ListItemButton
              key={userItem._id}
              onClick={() => handleUserCheck(userItem)}
            >
              <ListItemAvatar sx={{ mt: 0 }}>
                <Avatar
                  alt={userItem.firstname}
                  src={userItem.avatar}
                  sx={avatarStyles}
                />
              </ListItemAvatar>
              <ListItemText
                primary={fullName}
                className="ellipsis-text one-line"
              />
              <Checkbox
                edge="end"
                checked={users.some((item) => item._id === userItem._id)}
                tabIndex={-1}
                disableRipple
              />
            </ListItemButton>
          );
        })}
      </List>
      <Stack alignItems="center">
        <LoadingButton
          loading={isLoading}
          disabled={!users.length}
          onClick={handleStartConversation}
          sx={{
            m: 2,
            fontWeight: 600,
            backgroundColor: TP_COLORS.border.color1,
            color: TP_COLORS.gray.darken,
          }}
        >
          Start Chat
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

export default NewConversation;
